import { formatDate } from "@angular/common";
import { inject, Injectable } from '@angular/core';
import { ExportTypeEnum } from "@models/common/export-type.enum";
import { Tag } from "@models/common/tag";
import { Company } from "@models/companies/company";
import { CompanyStatus } from "@models/companies/company-enums";
import { CompanyRecord } from "@models/companies/company-record";
import { ColumnWithExportName } from "@models/grid/column-with-export-name";
import { FilterOption, FilterParams } from "@models/grid/grid-filter";
import {
  CommonActionsModalComponent,
  IActionButtonConfig
} from "@shared/components/commons/common-actions-modal/common-actions-modal.component";
import {
  ExportEntityListModalComponent
} from "@shared/components/commons/export-company-list-modal/export-entity-list-modal.component";
import { ACNPipe } from "@shared/pipes/acnPipe";
import { CompaniesApiService } from "@shared/services/companies-api.service";
import { excelReportTitleStyles, excelTitleForReport } from "@shared/utils/excel-title-for-report";
import {
  ColDef,
  CsvExportParams,
  ExcelExportParams,
  ExcelRow,
  ExcelStyle,
  GridApi,
  ProcessCellForExportParams,
  ProcessHeaderForExportParams
} from "ag-grid-community";
import { deepClone } from "fast-json-patch/commonjs/core";
import { ToastrService } from "ngx-toastr";
import { catchError, from, of, switchMap, take, tap } from "rxjs";
import { ModalFormsService } from "../../../forms/services/modal-forms.service";

@Injectable()
export class CompanyExportService {
  companiesApiService = inject(CompaniesApiService);
  modalFormsService = inject(ModalFormsService);
  toastr = inject(ToastrService);

  // <colDef.field, exportColumnName>
  readonly exportColumnNameDictionary: Record<string, string> = {
    name: 'Company Name',
    acn: 'ACN',
    dateOfEstablishment: 'Registration Date'
  };
  readonly excelStyles: ExcelStyle[] = excelReportTitleStyles;

  getExportParamsXls(numberColumnForExport: number = 1): ExcelExportParams {
    return {
      ...this.getCompaniesExportParams(),
      prependContent: this.getExcelTitleRows(numberColumnForExport)
    } as ExcelExportParams;
  }

  getExportParamsCsv(): CsvExportParams {
    return this.getCompaniesExportParams() as CsvExportParams;
  }

  getExcelTitleRows(numberColumnForExport: number): ExcelRow[] {
    return excelTitleForReport(numberColumnForExport, 'Companies Report');
  }

  getCompaniesExportParams() {
    const processCellCallback = (params: ProcessCellForExportParams): string => {
      const value: unknown = params.value;
      const companyData = params.node?.data as Company;

      if (params.column.getColId() === 'name') {
        return companyData.name;
      } else if (params.column.getColId() === 'acn') {
        return ACNPipe.formatValue(companyData.acn);
      } else if (params.column.getColId() === 'status') {
        return CompanyStatus[value as CompanyStatus];
      } else if (params.column.getColId() === 'tags') {
        return (value as Tag[])?.map((tag: Tag) => tag.name)?.join(', ');
      } else if (params.column.getColId() === 'systemTags') {
        return (value as Tag[])?.map((tag: Tag) => tag.name)?.join(', ');
      } else if (params.column.getColId() === 'dateOfEstablishment' || params.column.getColId() === 'nextAnnualReviewDate') {
        return formatDate(value as string ?? '', 'dd MMM yyyy', 'en-US');
      } else if (params.column.getColId() === 'jurisdiction') {
        const filterParams = params.column.getColDef()?.filterParams as { filterOptions?: FilterOption[] };
        const options: FilterOption[] = filterParams?.filterOptions ?? [];
        return options.find(o => o.value == value)?.label ?? '';
      }

      return value as string;
    };

    const processHeaderCallback = (params: ProcessHeaderForExportParams) => {
      const colDef = params.column.getColDef() as ColDef;

      if (colDef.field && colDef.field in this.exportColumnNameDictionary) {
        return this.exportColumnNameDictionary[colDef.field];
      }

      return colDef.headerName;
    };

    return {
      processCellCallback,
      processHeaderCallback,
      sheetName: 'Company',
    };
  }

  exportCompanyList(gridApi: GridApi, isBulkExport: boolean, exportType: ExportTypeEnum, colDefs: ColDef[], checkedCompanies: CompanyRecord[]): void {
    let numberColumnForExport = colDefs.length - 1;
    const count = isBulkExport ? checkedCompanies.length : checkedCompanies.length;

    const modalRef = this.modalFormsService.openModal(ExportEntityListModalComponent, { modalDialogClass: 'export-company-list' });
    const componentInstance = modalRef.componentInstance as ExportEntityListModalComponent;

    componentInstance.colDefs = deepClone(colDefs) as ColumnWithExportName[];
    componentInstance.numberOfCompanies = count;
    componentInstance.exportType = exportType;

    componentInstance.confirm
      .pipe(take(1))
      .subscribe((columnForExport: string[]) => {
        const nameIndex = columnForExport.findIndex(field => field === 'name');

        if (nameIndex >= 0) {
          columnForExport.splice(nameIndex + 1, 0, 'acn');
        }

        const tagIndex = columnForExport.findIndex(field => field === 'tags');

        if (tagIndex >= 0) {
          columnForExport.splice(tagIndex + 1, 0, 'systemTags');
        }

        numberColumnForExport = columnForExport.length - 1;

        const noun = count > 1 ? 'Companies' : 'Company';
        const statusFilter = this.getSelectedFilterOptions(gridApi, 'status');
        const prefix = statusFilter ? `${ statusFilter } ` : '';
        const fileName = `${ prefix }${ noun } ${ formatDate(new Date(), 'dd-MM-yyyy', 'en-US') }`;

        if (exportType === ExportTypeEnum.EXCEL) {
          const params: ExcelExportParams = isBulkExport
            ? {
              columnKeys: columnForExport,
              onlySelected: true
            }
            : {
              columnKeys: columnForExport
            };
          const exportParamsXls = this.getExportParamsXls(numberColumnForExport);

          gridApi.exportDataAsExcel({ ...exportParamsXls, ...params, fileName });
        } else if (exportType === ExportTypeEnum.CSV) {
          const params: CsvExportParams = isBulkExport
            ? {
              columnKeys: columnForExport,
              onlySelected: true
            }
            : {
              columnKeys: columnForExport
            };
          gridApi.exportDataAsCsv({ ...params, fileName });
        }
      });
  }

  getSelectedFilterOptions(gridApi: GridApi, columnId: string): string {
    if (!gridApi || gridApi.isDestroyed()) return '';

    const filterParams = gridApi.getColumn(columnId)?.getColDef().filterParams as FilterParams;
    const filterOptions = filterParams?.filterOptions ?? [];
    const selectedFilterOptions = filterOptions
      .filter((filterOption) => filterOption.active)
      .map((filterOption) => filterOption.label)
      .join(', ');

    return selectedFilterOptions;
  }

  exportCompanyProfile(gridApi: GridApi, company: CompanyRecord, colDefs: ColDef[]): void {
    const columnForExport = colDefs
      .filter((colDef) => !colDef.hide && colDef.headerName)
      .map((colDef) => colDef.field) as string[];

    const nameIndex = columnForExport.findIndex(field => field === 'name');

    if (nameIndex >= 0) {
      columnForExport.splice(nameIndex + 1, 0, 'acn');
    }

    const tagIndex = columnForExport.findIndex(field => field === 'tags');

    if (tagIndex >= 0) {
      columnForExport.splice(tagIndex + 1, 0, 'systemTags');
    }

    const numberColumnForExport = columnForExport.length - 1;
    const exportParamsXls = this.getExportParamsXls(numberColumnForExport);

    gridApi.exportDataAsExcel({
      ...exportParamsXls,
      columnKeys: columnForExport,
      onlySelected: true
    });
  }

  downloadCompanyProfilePdf(company: CompanyRecord): void {
    const fileName = `Company Profile - ${ company.name }.pdf`;
    const showDownloadingMessage = () => this.toastr.info(`Downloading company profile report for ${ company.name }`);
    const openDownloadCompanyProfileWithUnlodgedChangesModal = () => {
      const modalRef = this.modalFormsService.openModal(CommonActionsModalComponent, { size: 'sm' }, '');
      const instance = modalRef.componentInstance as CommonActionsModalComponent;

      const body = `
        <div class="text-center">This company has unlodged changes.</div>
        <div class="text-center">Do you want to include them in the company profile report?</div>
    `;
      const actions: IActionButtonConfig[] = [
        {
          // proceed with download and include unlodged changes
          text: 'Yes',
          btnColor: 'orange',
          action: of(true)
        },
        {
          // proceed with download excluding unlodged changes
          text: 'No',
          btnColor: 'orange',
          action: of(false)
        },
      ];

      instance.config = {
        ...instance.config,
        header: 'Include Unlodged Changes',
        body,
        actions,
        showCloseBtn: true,
        closeBtnWrapperClass: 'flex-grow-1 d-flex justify-content-end',
        closeBtnText: 'Cancel',
        cancelAction: of(null).pipe(tap(() => instance.close()))
      };

      return from(modalRef.result)
        .pipe(
          take(1),
          switchMap((result: boolean | null) => {
            if (result == null) {
              return of(null);
            }

            showDownloadingMessage();
            return this.companiesApiService.generatePdfProfile(company.entityId, fileName, result);
          }),
          catchError(() => of(null))
        );
    };

    this.companiesApiService.hasPendingChanges(company.entityId)
      .pipe(
        switchMap((companyHasPendingChanges) => {
          if (companyHasPendingChanges) {
            return openDownloadCompanyProfileWithUnlodgedChangesModal();
          }

          showDownloadingMessage();
          return this.companiesApiService.generatePdfProfile(company.entityId, fileName);
        })
      )
      .subscribe();
  }
}
